/**
 * Store Pinia des mouvements de stock (composition API)
 */
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { IStockMovement } from "../models/stockMovement";
import { IStockMovementFilter } from "../models/filters/stockMovementFilter";
import StockMovementService from "../services/StockMovementService";
import { IStockMovementForm } from "../models/forms/stockMovementForm";
import { IStockMovementApiResponse } from "../models/api/stockMovementApiResponse";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import { ICenter } from "../models/center";
import { IMovementType } from "../models/movementType";
import { IStockUnity } from "../models/stockUnity";
import { isNumeric } from "../helper/stringHelper";
import { IArticleStockUnityMovementForm } from "../models/forms/articleForm";
import { GroupedStock } from "../helper/type";
import { useCenterStore } from "./center.store";
import { useStockUnityStore } from "./stockUnity.store";
import { useMovementTypeStore } from "./movementType.store";
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";

export const useStockMovementStore = defineStore("stockmovements", () => {
    // State
    const datas = ref<IStockMovement[]>([]);
    const current = ref<IStockMovement>({} as IStockMovement);
    const itemForm = ref<IStockMovementForm>({} as IStockMovementForm);
    const itemFormStock = ref<IArticleStockUnityMovementForm>({} as IArticleStockUnityMovementForm);
    const isLoading = ref<boolean>(false);

    // Getters
    const getMouvementSemence = computed(() => () => {
        return datas.value.filter((mouvement) => !isNumeric(mouvement.unity.article?.code) && mouvement.unity.article.code);
    });

    const getMouvementSemenceArticle = computed(() => (idArticle: number, stockGroupSelected: GroupedStock) => {
        return datas.value.filter((mouvement) =>
            !isNumeric(mouvement.unity.article?.code) &&
            mouvement.unity.article.code &&
            mouvement.unity.article.id == idArticle &&
            mouvement.unity.volumeDose == stockGroupSelected.volumeDose &&
            mouvement.unity.spzDose == stockGroupSelected.spzDose &&
            Boolean(mouvement.unity.heterospermie) == stockGroupSelected.isHeterospermie &&
            mouvement.unity.conditionnement?.code3 == stockGroupSelected.conditionnementCode &&
            mouvement.unity.isArmada == stockGroupSelected.isArmada
        );
    });

    const getMouvementSemenceSchema = computed(() => (codeSchema: string, stockGroupSelected: GroupedStock) => {
        return datas.value.filter((mouvement) =>
            !isNumeric(mouvement.unity.article?.code) &&
            mouvement.unity.article.code &&
            mouvement.unity.article?.geneticType?.code3 == codeSchema &&
            mouvement.unity.volumeDose == stockGroupSelected.volumeDose &&
            mouvement.unity.spzDose == stockGroupSelected.spzDose &&
            Boolean(mouvement.unity.heterospermie) == stockGroupSelected.isHeterospermie &&
            mouvement.unity.conditionnement?.code3 == stockGroupSelected.conditionnementCode &&
            mouvement.unity.isArmada == stockGroupSelected.isArmada
        );
    });

    // Actions - Hydration
    /**
     * Hydrate une liste de mouvements de stock en batch (optimisé pour la performance)
     * - Charge centerStore, stockUnityStore et movementTypeStore si nécessaires
     * - Crée des Maps d'index pour des lookups O(1)
     */
    async function hydrateStockMovements(items: IStockMovementApiResponse[]): Promise<IStockMovement[]> {
        if (items.length === 0) return [];

        // 1. Récupérer les stores dépendants
        const centerStore = useCenterStore();
        const stockUnityStore = useStockUnityStore();
        const movementTypeStore = useMovementTypeStore();

        // 2. Charger les stores dépendants en parallèle si nécessaire
        const loadPromises: Promise<unknown>[] = [];

        if (centerStore.datas.length === 0) {
            loadPromises.push(centerStore.load({ page: 1, pageSize: 0 }));
        }
        if (stockUnityStore.datas.length === 0) {
            loadPromises.push(stockUnityStore.load({ page: 1, pageSize: 0 }));
        }
        if (movementTypeStore.datas.length === 0) {
            loadPromises.push(movementTypeStore.load({ page: 1, pageSize: 0 }));
        }

        if (loadPromises.length > 0) {
            await Promise.all(loadPromises);
        }

        // 3. Créer des Maps pour des lookups O(1)
        const centerMap = new Map(centerStore.datas.map(c => [c.id, c]));
        const stockUnityMap = new Map(stockUnityStore.datas.map(u => [u.id, u]));
        const movementTypeMap = new Map(movementTypeStore.datas.map(m => [m.id, m]));

        // 4. Hydrater tous les mouvements en une seule passe
        const models: IStockMovement[] = [];
        for (const item of items) {
            const model = { ...item } as unknown as IStockMovement;

            // Lier les références (lookup O(1) via Map)
            model.center = centerMap.get(item.centerId)!;
            model.unity = stockUnityMap.get(item.unityId)!;
            model.movementType = movementTypeMap.get(item.movementTypeId)!;

            models.push(model);
        }

        return models;
    }

    // Actions

    /**
     * Chargement des données dans le store
     */
    async function load(filter: IStockMovementFilter) {
        isLoading.value = true;
        const result = await StockMovementService.getAll<IStockMovementFilter, IStockMovementApiResponse>(filter);
        isLoading.value = false;
        datas.value = [];

        if (!result.error) {
            datas.value = await hydrateStockMovements(result.data as IStockMovementApiResponse[]);
        }

        return result;
    }

    /**
     * Récupération d'un mouvement de stock par son identifiant
     */
    async function get(id: number) {
        const result = await StockMovementService.getById<IStockMovementApiResponse>(id);

        if (!result.error) {
            const [hydratedItem] = await hydrateStockMovements([result.data as IStockMovementApiResponse]);

            const index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0)
                datas.value[index] = hydratedItem;

            current.value = hydratedItem;
            itemForm.value = toForm(hydratedItem);
        }

        return result;
    }

    /**
     * Récupération de l'objet dans le store
     */
    async function searchStoreById(id: number): Promise<IStockMovement> {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = toForm(current.value as IStockMovement);
        return current.value;
    }

    /**
     * Mise à jour ou création d'un objet
     */
    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result = {} as ServiceOperationResult<IStockMovement | IDataOperationResult<IStockMovement>>;

        if (itemForm.value.id > 0) {
            result = await StockMovementService.update(itemForm.value);

            if (!result.error) {
                const [hydratedItem] = await hydrateStockMovements([result.data as unknown as IStockMovementApiResponse]);
                current.value = hydratedItem;

                if (maj) {
                    const index = datas.value.findIndex((a) => a.id == itemForm.value.id);
                    if (index >= 0) {
                        datas.value[index] = current.value;
                    } else {
                        const error = new DataOperationError();
                        error.REQUEST_ERROR = "Item non trouvé dans le store";
                        return new ServiceOperationResult<IDataOperationResult<IStockMovement>>(true, { errors: error });
                    }
                }
            }
        } else {
            result = await StockMovementService.create(itemForm.value);

            if (!result.error) {
                const [hydratedItem] = await hydrateStockMovements([result.data as unknown as IStockMovementApiResponse]);
                current.value = hydratedItem;
                if (datas.value.findIndex((a) => a.id == current.value?.id) == -1) {
                    datas.value.push(current.value);
                }
            }
        }

        return result;
    }

    async function saveStock(maj: boolean = true) {
        if (itemFormStock.value.id > 0) {
            return {} as ServiceOperationResult<IDataOperationResult<IStockMovement>>;
        }
        return StockMovementService.stockMovement(itemFormStock.value as unknown as IStockMovementForm);
    }

    /**
     * Sauvegarde un mouvement de stock à partir d'un formulaire passé en paramètre.
     * Contrairement à saveStock(), n'utilise pas l'état partagé itemFormStock,
     * ce qui permet d'appeler cette méthode en parallèle sans race condition.
     */
    async function saveStockForm(form: IArticleStockUnityMovementForm) {
        if (form.id > 0) {
            return {} as ServiceOperationResult<IDataOperationResult<IStockMovement>>;
        }
        return StockMovementService.stockMovement(form as unknown as IStockMovementForm);
    }

    /**
     * Delete de l'objet
     */
    async function deleteAction() {
        const result = await StockMovementService.delete<IStockMovementApiResponse>(current.value.id);

        if (!result.error) {
            const index = datas.value.findIndex((a) => a.id == current.value.id);
            if (index >= 0) {
                datas.value.splice(index, 1);
            }

            current.value = newItem({ id: 0, name: "", code: "", isActive: true } as ICenter);
            itemForm.value = newForm(0);
        }

        return result;
    }

    /**
     * Transformation de l'objet en formulaire
     */
    function toForm(item: IStockMovement): IStockMovementForm {
        const form = {} as IStockMovementForm;
        form.id = item.id;
        form.code = item.code;
        form.centerId = item.center.id;
        form.quantity = item.quantity;
        form.unityId = item.unity.id;
        form.movementTypeId = item.movementType.id;
        form.movementDate = item.movementDate;
        form.movementUser = item.movementUser;
        form.workStation = item.workStation;
        itemForm.value = form;
        return form;
    }

    /**
     * Transformation du formulaire stock
     */
    function toFormStock(item: IArticleStockUnityMovementForm): IArticleStockUnityMovementForm {
        const form = {} as IArticleStockUnityMovementForm;
        form.id = item.id;
        form.isMovement = item.isMovement;
        form.way = item.way;
        form.stockCode = item.stockCode;
        form.articleCode = item.articleCode;
        form.centerCode = item.centerCode;
        form.articleLibelle = item.articleLibelle;
        form.quantity = item.quantity;
        form.lotNumber = item.lotNumber;
        form.datePeremption = item.datePeremption;
        form.aptitudeConservationCode = item.aptitudeConservationCode;
        form.qualityGeneticCode = item.qualityGeneticCode;
        form.conditionnementCode = item.conditionnementCode;
        form.productionDate = item.productionDate;
        form.isBillable = item.isBillable;
        form.dateEvent = item.dateEvent;
        form.cTime = item.cTime;
        form.productionUID = item.productionUID;
        form.manufactoringUID = item.manufactoringUID;
        form.productionKey = item.productionKey;
        form.manufactoringKey = item.manufactoringKey;
        form.productionNumber = item.productionNumber;
        form.isArmada = item.isArmada;
        form.nnCode = item.nnCode;
        form.infoCenter = item.infoCenter;
        form.isRefMax = item.isRefMax;
        form.indexMax = item.indexMax;
        form.indexMaxRe = item.indexMaxRe;
        form.volumeDose = item.volumeDose;
        form.spzDose = item.spzDose;
        form.heterospermie = item.heterospermie;
        form.dispatchId = item.dispatchId;
        itemFormStock.value = form;
        return form;
    }

    /**
     * Permet d'initialiser un objet vide pour le formulaire
     */
    function newForm(lienId: number): IStockMovementForm {
        itemForm.value = {
            id: 0,
            centerId: lienId,
            code: "",
            quantity: 0,
            unityId: 0,
            movementTypeId: 0,
            movementDate: new Date(),
            movementUser: "",
            workStation: "",
        } as IStockMovementForm;
        return itemForm.value;
    }

    /**
     * Permet d'initialiser un objet vide pour les referentiels
     */
    function newItem(lien: ICenter): IStockMovement {
        current.value = {
            id: 0,
            code: "",
            quantity: 0,
            center: lien,
            movementDate: new Date(),
            movementUser: "",
            workStation: "",
            unity: { id: 0 } as IStockUnity,
            movementType: { id: 0 } as IMovementType,
        } as IStockMovement;
        itemForm.value = toForm(current.value);
        return current.value;
    }

    return {
        // State
        datas,
        current,
        itemForm,
        itemFormStock,
        isLoading,
        // Getters
        getMouvementSemence,
        getMouvementSemenceArticle,
        getMouvementSemenceSchema,
        // Actions
        hydrateStockMovements,
        load,
        get,
        searchStoreById,
        save,
        saveStock,
        saveStockForm,
        delete: deleteAction,
        toForm,
        toFormStock,
        newForm,
        newItem,
    };
});

signalRManager.connection.on("MajStockMovement", async (payload: IPayloadStore) => {
    const store = useStockMovementStore();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydrateStockMovements([payload.data as IStockMovementApiResponse]);
                store.datas.push(hydrated);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateStockMovements([payload.data as IStockMovementApiResponse]);
                store.datas.splice(idx, 1, hydrated);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});
